/**
 * Home page of the public site (canvas "Gravel Games — Site 2027", artboard "Accueil"), FR + EN.
 * Editorial copy only: formats, prices, tiers, dates of sale come from the API
 * (`GET /events/:slug`). `{{…}}` placeholders are filled by the page.
 */
export const siteHome = {
  fr: {
    documentTitle: 'Gravel Games — gravel running et gravel cycling sur le Larzac',
    hero: {
      open: 'Inscriptions ouvertes',
      earlyBird: "Early bird jusqu'au {{date}}",
      title: 'Deux jours. Deux disciplines. Un terrain de jeu exceptionnel.',
      sub: 'Habituellement fermé au public.',
      place: 'Camp militaire du Larzac · Aveyron',
      engage: "Je m'engage",
      seeEvents: 'Voir les épreuves',
      soundOn: 'Activer le son de la vidéo',
      soundOff: 'Couper le son de la vidéo',
    },
    stats: [
      { value: '2 jours', label: 'les 3 et 4 avril 2027' },
      { value: '2 disciplines', label: 'gravel running et gravel cycling' },
      { value: '{{count}} formats', label: "du 5 km à l'endurance 12 heures" },
      { value: '1 terrain', label: "fermé au public le reste de l'année" },
    ],
    concept: {
      eyebrow: 'Le concept',
      title: ['One ground.', 'Many ways to play.'],
      motto: 'Un même terrain. Mille façons de le vivre.',
      paragraphs: [
        'Les Gravel Games célèbrent la culture gravel : rouler, courir, explorer et partager, sur un terrain que personne ne foule d’habitude.',
        'Ici, le terrain est le personnage principal. Le parcours raconte le Larzac, sa lumière, son relief, ses pistes. On n’y vient pas pour le conquérir, on y est invité.',
        'Exigeant sans être excluant : de la découverte à la haute performance, chacun trouve sa place. Seul, en duo ou en équipe.',
      ],
      pillars: [
        {
          icon: 'bike',
          title: 'Rouler',
          text: 'Une rando de 35 km et deux courses de 70 et 100 km sur les pistes du camp.',
        },
        {
          icon: 'run',
          title: 'Courir',
          text: 'Trois distances en gravel running, de 5 à 20 km, pour découvrir ou performer.',
        },
        {
          icon: 'both',
          title: 'Les deux',
          text: 'Run & Ride en binôme, 12H Endurance en équipe, ou plusieurs formats sur le week-end.',
        },
      ],
    },
    events: {
      eyebrow: 'Les épreuves {{edition}}',
      title: 'Choisis ton terrain de jeu',
      sub: 'Tu peux t’engager sur plusieurs formats.',
      earlyBird: "Early bird jusqu'au {{date}}",
      then: 'puis {{price}}',
      perPerson: '/ pers.',
      elevation: '{{value}} m D+',
      conditions: 'Conditions de participation →',
      engage: "Je m'engage",
      loading: 'Chargement des épreuves…',
      error: 'Les épreuves n’ont pas pu être chargées. Réessaie dans un instant.',
      groups: {
        cycling: {
          kicker: 'Cycling',
          title: 'Gravel Cycling',
          text: 'Une rando pour découvrir, deux courses pour se mesurer au Larzac.',
        },
        running: {
          kicker: 'Running',
          title: 'Gravel Running',
          text: 'Trois distances pour courir les pistes du camp, de la découverte à la performance.',
        },
        duo: {
          kicker: 'Binôme · Running + Cycling',
          title: 'Run & Ride',
          text: 'Épreuve bike & run par équipe de deux participants.',
          note: 'Âge minimum par équipier : 16 ans (12 km), 18 ans (24 km). Cadre FFTRI : licence FFTRI compétition valable ou, à défaut, Licence Expérience FFTRI, pour chaque équipier. Modalités communiquées lors de l’inscription.',
        },
        team: {
          kicker: 'Équipe · Running + Cycling',
          title: '12H Endurance',
          text: 'Douze heures de gravel running et cycling, par équipe de 2 à 6 personnes.',
          perks: ['Repas inclus', 'Ravitaillement continu', 'Chronométrage 12 h'],
        },
      },
      legendPrice: 'Prix en orange',
      legend: '= tarif actuel, suivi du tarif normal. Tarifs majorés à l’approche de l’événement.',
      documents:
        'Licence ou justificatif de santé à fournir après l’inscription, selon l’épreuve (sauf Rando 35 km pour les majeurs).',
      check: {
        title: 'Puis-je participer ?',
        text: 'Âge minimum, licence, certificat médical, autorisation pour les mineurs : vérifie en moins d’une minute ce qu’il te faut pour prendre le départ.',
        cta: 'Vérifier mes conditions',
      },
    },
    crew: {
      eyebrow: 'Embarque ta team',
      title: 'Plus on est nombreux, plus tu as de chances',
      text: 'Une fois engagé·e, invite tes amis à te rejoindre sur le Larzac. Chaque ami qui s’engage te donne une chance de plus au tirage au sort des produits Gravel Games.',
      steps: [
        { title: 'Engage-toi', text: 'Choisis ton épreuve et valide ton inscription.' },
        {
          title: 'Invite tes amis',
          text: 'Partage ton lien personnel depuis ton espace participant.',
        },
        {
          title: 'Cumule tes chances',
          text: 'Chaque ami engagé = une chance de plus au tirage.',
        },
      ],
      prizesEyebrow: 'Les lots à gagner',
      prizesTitle: 'Dévoilés prochainement',
      prizesText: 'Des produits Gravel Games à remporter par tirage au sort.',
      rulesTitle: 'Règlement du jeu concours',
      rulesText: 'Disponible sur demande écrite à {{email}} ou par courrier à :',
      email: 'contact@wishonecycles.com',
      address: ['WISH ONE RACING', 'Avenue de Saint-Affrique', '12100 Creissels'],
    },
    info: {
      eyebrow: 'Infos pratiques',
      title: 'Rendez-vous sur le Larzac',
      cards: [
        {
          title: 'Lieu',
          lines: [
            'Camp général de Castelnau',
            '(camp militaire du Larzac)',
            'La Cavalerie, Aveyron',
          ],
        },
        {
          title: 'Programme',
          lines: [
            '**Sam. 3 avril, 8 h** : 12H Endurance',
            '**Dim. 4 avril, 8 h** : Gravel Cycling 70 et 100 km',
            '**Dim. 4 avril, 14 h** : Gravel Running et Run & Ride',
          ],
        },
        { title: 'Village & parking', lines: ['Village, accès et stationnement : à venir.'] },
        { title: 'Hébergement', lines: ['Solutions à proximité du camp : à venir.'] },
      ],
      mapTitle: 'Camp militaire du Larzac',
      mapPlace: 'La Cavalerie, Aveyron',
      mapCta: 'Itinéraire Google Maps',
      mapUrl:
        'https://www.google.com/maps/search/?api=1&query=Camp+militaire+du+Larzac+La+Cavalerie',
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Questions fréquentes',
      items: [
        {
          q: 'Peut-on faire le running et le cycling ?',
          a: 'Oui. Tu peux t’engager sur plusieurs formats lors de la même inscription, par exemple le 70 km le matin et le 10 km l’après-midi.',
        },
        {
          q: 'Comment fonctionne la 12H Endurance ?',
          a: 'Une épreuve de gravel running et cycling par équipe de 2 à 6 personnes, au départ le samedi 3 avril à 8 h. Le tarif est par personne, repas, ravitaillement continu et chronométrage compris.',
        },
        {
          q: 'Comment fonctionne l’Endurance 12h Gravel Running & Cycling ?',
          a: 'L’Endurance 12h est une épreuve collective qui associe course à pied et vélo gravel, par équipes de 2 à 6 participants. Pendant 12 heures, chaque équipe doit avoir en permanence au moins un coureur à pied et un cycliste sur les parcours, à l’exception des temps de transmission des relais. Deux boucles distinctes sont proposées : environ 5 km en Gravel Running et 20 km en Gravel Cycling. Les équipiers sont libres de s’organiser, de se relayer et d’alterner entre les deux disciplines selon leur stratégie. L’objectif est simple : cumuler le plus grand nombre de tours possible en running et en cycling pendant les 12 heures. Gestion de l’effort, stratégie des relais, endurance et esprit d’équipe seront les clés de cette aventure !',
        },
        {
          q: 'Faut-il obligatoirement pratiquer les deux disciplines pendant l’Endurance 12h ?',
          a: 'Non ! Chaque participant est libre de se consacrer uniquement au Gravel Running, au Gravel Cycling, ou d’alterner entre les deux disciplines. L’équipe, composée de 2 à 6 participants, organise librement ses relais et sa stratégie pendant les 12 heures. La seule obligation est d’avoir en permanence au moins un coureur à pied et un cycliste sur les parcours, à l’exception des transmissions de relais. Les autres équipiers peuvent se reposer ou récupérer avant de repartir. À chacun de trouver la meilleure organisation pour réaliser le maximum de tours !',
        },
        {
          q: 'Quels justificatifs fournir ?',
          a: 'Selon l’épreuve, une licence ou un justificatif de santé, à fournir après ton inscription (sauf pour les majeurs sur la Rando 35 km). Pour le Run & Ride, chaque équipier doit disposer d’une licence FFTRI compétition valable ou, à défaut, d’une Licence Expérience FFTRI, dont les modalités pratiques sont communiquées lors de l’inscription. Le détail figure dans les conditions de participation.',
        },
        {
          q: 'Comment marche le tirage au sort ?',
          a: 'Chaque ami qui s’engage grâce à ton lien te donne une chance de plus. Les lots seront dévoilés prochainement. Le règlement est disponible sur demande écrite à contact@wishonecycles.com ou à WISH ONE RACING, Avenue de Saint-Affrique, 12100 Creissels.',
        },
        {
          q: 'Jusqu’à quand puis-je m’inscrire ?',
          a: 'Jusqu’au jour J, dans la limite des places. Les prix augmentent par paliers : early bird jusqu’au 30 novembre, tarif normal jusqu’au 28 février, puis dernière minute et jour J.',
        },
      ],
    },
    final: {
      title: 'One ground. Many ways to play.',
      place: 'Camp militaire du Larzac',
      engage: "Je m'engage",
    },
  },
  en: {
    documentTitle: 'Gravel Games — gravel running and gravel cycling on the Larzac',
    hero: {
      open: 'Registration open',
      earlyBird: 'Early bird until {{date}}',
      title: 'Two days. Two disciplines. One exceptional playground.',
      sub: 'Usually closed to the public.',
      place: 'Larzac military camp · Aveyron',
      engage: 'Sign up',
      seeEvents: 'See the events',
      soundOn: 'Turn the video sound on',
      soundOff: 'Turn the video sound off',
    },
    stats: [
      { value: '2 days', label: 'April 3 and 4, 2027' },
      { value: '2 disciplines', label: 'gravel running and gravel cycling' },
      { value: '{{count}} formats', label: 'from 5 km to the 12-hour endurance' },
      { value: '1 ground', label: 'closed to the public the rest of the year' },
    ],
    concept: {
      eyebrow: 'The concept',
      title: ['One ground.', 'Many ways to play.'],
      motto: 'Un même terrain. Mille façons de le vivre.',
      paragraphs: [
        'The Gravel Games celebrate gravel culture: riding, running, exploring and sharing, on ground nobody usually sets foot on.',
        'Here the ground is the main character. The course tells the story of the Larzac, its light, its relief, its tracks. You don’t come to conquer it: you are its guest.',
        'Demanding without being exclusive: from first-timers to high performance, everyone has a place. Solo, as a pair or as a team.',
      ],
      pillars: [
        {
          icon: 'bike',
          title: 'Ride',
          text: 'A 35 km tour and two races of 70 and 100 km on the camp’s tracks.',
        },
        {
          icon: 'run',
          title: 'Run',
          text: 'Three gravel running distances, from 5 to 20 km, to discover or to perform.',
        },
        {
          icon: 'both',
          title: 'Both',
          text: 'Run & Ride as a pair, 12H Endurance as a team, or several formats over the weekend.',
        },
      ],
    },
    events: {
      eyebrow: 'The {{edition}} events',
      title: 'Pick your playground',
      sub: 'You can sign up for several formats.',
      earlyBird: 'Early bird until {{date}}',
      then: 'then {{price}}',
      perPerson: '/ person',
      elevation: '{{value}} m elevation',
      conditions: 'Entry requirements →',
      engage: 'Sign up',
      loading: 'Loading the events…',
      error: 'The events could not be loaded. Please try again in a moment.',
      groups: {
        cycling: {
          kicker: 'Cycling',
          title: 'Gravel Cycling',
          text: 'A tour to discover, two races to take on the Larzac.',
        },
        running: {
          kicker: 'Running',
          title: 'Gravel Running',
          text: 'Three distances to run the camp’s tracks, from discovery to performance.',
        },
        duo: {
          kicker: 'Pair · Running + Cycling',
          title: 'Run & Ride',
          text: 'Bike & run event for teams of two.',
          note: 'Minimum age per teammate: 16 (12 km), 18 (24 km). FFTRI framework: a valid FFTRI competition licence or, failing that, an FFTRI Experience Licence, for each teammate. Details given at registration.',
        },
        team: {
          kicker: 'Team · Running + Cycling',
          title: '12H Endurance',
          text: 'Twelve hours of gravel running and cycling, in teams of 2 to 6.',
          perks: ['Meal included', 'Continuous aid station', '12-hour timing'],
        },
      },
      legendPrice: 'Price in orange',
      legend:
        '= current price, followed by the standard price. Prices rise as the event gets closer.',
      documents:
        'Licence or health certificate to provide after registration, depending on the event (except the 35 km tour for adults).',
      check: {
        title: 'Can I take part?',
        text: 'Minimum age, licence, medical certificate, consent for minors: check in under a minute what you need to start.',
        cta: 'Check my requirements',
      },
    },
    crew: {
      eyebrow: 'Bring your crew',
      title: 'The more of you, the better your chances',
      text: 'Once you’re in, invite your friends to join you on the Larzac. Every friend who signs up gives you one more chance in the Gravel Games prize draw.',
      steps: [
        { title: 'Sign up', text: 'Pick your event and complete your registration.' },
        { title: 'Invite your friends', text: 'Share your personal link from your account.' },
        {
          title: 'Stack your chances',
          text: 'Every friend who signs up = one more chance in the draw.',
        },
      ],
      prizesEyebrow: 'Prizes',
      prizesTitle: 'Revealed soon',
      prizesText: 'Gravel Games products to win in a prize draw.',
      rulesTitle: 'Prize draw rules',
      rulesText: 'Available on written request to {{email}} or by post to:',
      email: 'contact@wishonecycles.com',
      address: ['WISH ONE RACING', 'Avenue de Saint-Affrique', '12100 Creissels, France'],
    },
    info: {
      eyebrow: 'Practical info',
      title: 'See you on the Larzac',
      cards: [
        {
          title: 'Venue',
          lines: ['Camp général de Castelnau', '(Larzac military camp)', 'La Cavalerie, Aveyron'],
        },
        {
          title: 'Programme',
          lines: [
            '**Sat. April 3, 8 am**: 12H Endurance',
            '**Sun. April 4, 8 am**: Gravel Cycling 70 and 100 km',
            '**Sun. April 4, 2 pm**: Gravel Running and Run & Ride',
          ],
        },
        { title: 'Village & parking', lines: ['Village, access and parking: coming soon.'] },
        { title: 'Accommodation', lines: ['Places to stay near the camp: coming soon.'] },
      ],
      mapTitle: 'Larzac military camp',
      mapPlace: 'La Cavalerie, Aveyron',
      mapCta: 'Directions on Google Maps',
      mapUrl:
        'https://www.google.com/maps/search/?api=1&query=Camp+militaire+du+Larzac+La+Cavalerie',
    },
    faq: {
      eyebrow: 'FAQ',
      title: 'Frequently asked questions',
      items: [
        {
          q: 'Can I do both running and cycling?',
          a: 'Yes. You can sign up for several formats in the same registration, for example the 70 km in the morning and the 10 km in the afternoon.',
        },
        {
          q: 'How does the 12H Endurance work?',
          a: 'A gravel running and cycling event for teams of 2 to 6, starting on Saturday April 3 at 8 am. The price is per person, meal, continuous aid station and timing included.',
        },
        {
          q: 'How does the 12h Endurance Gravel Running & Cycling work?',
          a: 'The 12h Endurance is a team event combining running and gravel cycling, for teams of 2 to 6. For 12 hours, each team must always have at least one runner and one cyclist out on the courses, except while handing over a relay. There are two separate loops: about 5 km of Gravel Running and 20 km of Gravel Cycling. Teammates are free to organise themselves, take turns and switch between the two disciplines as their strategy dictates. The goal is simple: complete as many running and cycling laps as possible in 12 hours. Pacing, relay strategy, endurance and team spirit will be the keys to this adventure!',
        },
        {
          q: 'Do I have to do both disciplines during the 12h Endurance?',
          a: 'No! Each participant is free to stick to Gravel Running, to Gravel Cycling, or to switch between the two. The team, made up of 2 to 6 participants, organises its relays and strategy freely over the 12 hours. The only rule is to always have at least one runner and one cyclist out on the courses, except during relay handovers. The other teammates can rest or recover before heading out again. It’s up to each team to find the best set-up to complete as many laps as possible!',
        },
        {
          q: 'Which documents do I need?',
          a: 'Depending on the event, a licence or a health certificate, to provide after registering (except for adults on the 35 km tour). For Run & Ride, each teammate needs a valid FFTRI competition licence or, failing that, an FFTRI Experience Licence, with practical details given at registration. The full details are in the entry requirements.',
        },
        {
          q: 'How does the prize draw work?',
          a: 'Every friend who signs up through your link gives you one more chance. Prizes will be revealed soon. The rules are available on written request to contact@wishonecycles.com or WISH ONE RACING, Avenue de Saint-Affrique, 12100 Creissels, France.',
        },
        {
          q: 'Until when can I register?',
          a: 'Until race day, while places last. Prices go up in steps: early bird until November 30, standard price until February 28, then last minute and race day.',
        },
      ],
    },
    final: {
      title: 'One ground. Many ways to play.',
      place: 'Larzac military camp',
      engage: 'Sign up',
    },
  },
} as const;

export type SiteHomeContent = (typeof siteHome)['fr'];
